import React, { useState, useRef } from 'react';
import groupService from '../services/groupService';
import useCountUpAnimation from '../hooks/useCountUpAnimation';
import Badge from './Badge';
import '../styles/group-ranking-card-modern.css';

const GroupRankingCardModern = ({
  ranking,
  currentUserId,
  isMobile,
  isGroupCreator,
  groupId,
  onUserRemoved
}) => {
  const [expanded, setExpanded] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [showDollarPL, setShowDollarPL] = useState(false);

  const isCurrentUser = ranking.userId === currentUserId;
  const canRemoveUser = isGroupCreator && !isCurrentUser;

  // Animated values
  const animatedNetWorth = useCountUpAnimation(ranking.netWorth || 0, 1000, 0);
  const animatedProfitLoss = useCountUpAnimation(ranking.profitLossPercent || 0, 1000, 1);
  const animatedProfitLossDollar = useCountUpAnimation(ranking.profitLoss || 0, 1000, 0);
  const animatedRank = useCountUpAnimation(ranking.rank || 0, 800, 0);
  const animatedCash = useCountUpAnimation(ranking.cash || 0, 1000, 0);
  const animatedPortfolio = useCountUpAnimation(ranking.portfolioValue || 0, 1000, 0);
  const animatedDeposit = useCountUpAnimation(ranking.deposit || 0, 1000, 0);
  const animatedLoan = useCountUpAnimation(ranking.loan || 0, 1000, 0);

  // Format currency (takes already-animated value)
  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}K`;
    } else {
      return `$${Math.round(amount) || '0'}`;
    }
  };

  // Format percentage
  const formatPercentage = (percent) => {
    if (percent === null || percent === undefined) return '+0.0%';
    return `${percent >= 0 ? '+' : ''}${percent.toFixed(1)}%`;
  };

  // Get rank display
  const getRankDisplay = (rank) => {
    switch (rank) {
      case 1: return { emoji: '🥇', isTop3: true };
      case 2: return { emoji: '🥈', isTop3: true };
      case 3: return { emoji: '🥉', isTop3: true };
      default: return { emoji: null, isTop3: false };
    }
  };

  const rankInfo = getRankDisplay(ranking.rank);
  const userName = ranking.nickname || `${ranking.firstName || ''} ${ranking.lastName || ''}`.trim() || ranking.username || 'Unknown';
  const userInitial = userName[0]?.toUpperCase() || '?';

  // Track touch position to distinguish tap from scroll
  const touchStartPos = useRef(null);
  const didToggleOnTouch = useRef(false);
  const plTouchStartPos = useRef(null);

  // Toggle expand
  const toggleExpand = () => {
    setExpanded(prev => !prev);
    if ('vibrate' in navigator && window.innerWidth <= 768) {
      navigator.vibrate(10);
    }
  };

  // Touch-based tap detection (fires BEFORE click on mobile)
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    touchStartPos.current = { x: touch.clientX, y: touch.clientY };
    didToggleOnTouch.current = false;
  };

  const handleTouchEnd = (e) => {
    if (!touchStartPos.current) return;
    const touch = e.changedTouches[0];
    const dx = Math.abs(touch.clientX - touchStartPos.current.x);
    const dy = Math.abs(touch.clientY - touchStartPos.current.y);

    if (dx < 15 && dy < 15) {
      // This was a tap, not a scroll
      e.preventDefault(); // Prevent subsequent click/mousedown/mouseup
      e.stopPropagation();
      didToggleOnTouch.current = true;
      toggleExpand();
    }
    touchStartPos.current = null;
  };

  // Click handler - only for desktop (mouse), skip if already handled by touch
  const handleCardClick = (e) => {
    e.stopPropagation();
    if (didToggleOnTouch.current) {
      didToggleOnTouch.current = false;
      return;
    }
    toggleExpand();
  };

  // P&L toggle touch handlers
  const handlePLTouchStart = (e) => {
    e.stopPropagation();
    const touch = e.touches[0];
    plTouchStartPos.current = { x: touch.clientX, y: touch.clientY };
  };

  const handlePLTouchEnd = (e) => {
    e.stopPropagation();
    if (!plTouchStartPos.current) return;
    const touch = e.changedTouches[0];
    const dx = Math.abs(touch.clientX - plTouchStartPos.current.x);
    const dy = Math.abs(touch.clientY - plTouchStartPos.current.y);
    if (dx < 15 && dy < 15) {
      e.preventDefault();
      setShowDollarPL(prev => !prev);
    }
    plTouchStartPos.current = null;
  };

  const handlePLClick = (e) => {
    e.stopPropagation();
    setShowDollarPL(prev => !prev);
  };

  // Format dollar P&L with sign
  const formatDollarPL = (amount) => {
    const sign = amount >= 0 ? '+' : '-';
    return `${sign}${formatCurrency(Math.abs(amount))}`;
  };

  // Handle user removal
  const handleRemoveUser = async (e) => {
    e.stopPropagation();

    const confirmed = window.confirm(`Are you sure you want to remove ${userName} from this group?`);
    if (!confirmed) return;

    setRemoving(true);

    try {
      const result = await groupService.removeUserFromGroup(groupId, ranking.userId);

      if (result.success) {
        if (onUserRemoved) {
          onUserRemoved(ranking.userId);
        }
      } else {
        throw new Error(result.error || 'Failed to remove user');
      }
    } catch (error) {
      console.error('Error removing user:', error);
      alert(`Failed to remove user: ${error.message}`);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div
      className={`ranking-card-modern ${isCurrentUser ? 'is-current-user' : ''} ${expanded ? 'expanded' : ''}`}
      onClick={handleCardClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{
        background: isCurrentUser ? 'linear-gradient(135deg, #FFFFFF 0%, #FEF2F2 100%)' : '#FFFFFF',
        borderColor: isCurrentUser ? '#D72638' : '#E5E7EB'
      }}
    >
      {/* Main Row */}
      <div className="ranking-card-main">
        {/* Rank */}
        <div className={`rank-column rank-${ranking.rank}`}>
          {rankInfo.emoji ? (
            <span className="rank-emoji">{rankInfo.emoji}</span>
          ) : (
            <span className="rank-number">#{animatedRank}</span>
          )}
        </div>

        {/* User Info */}
        <div className="user-column">
          <div className="user-avatar-modern">
            {ranking.avatar ? (
              <img src={ranking.avatar} alt={userName} />
            ) : (
              <span className="avatar-initial">{userInitial}</span>
            )}
          </div>
          <div className="user-info-modern">
            <div className="user-name-row">
              <span className="user-name-modern" style={{ color: '#111827' }}>{userName}</span>
              {isCurrentUser && <span className="you-tag">You</span>}
            </div>
            <span className="user-meta-modern" style={{ color: '#6B7280' }}>
              {ranking.daysActive || 0} days active
            </span>
          </div>
        </div>

        {/* Performance */}
        <div className="performance-column">
          <span className="net-worth-modern" style={{ color: '#111827' }}>{formatCurrency(animatedNetWorth)}</span>
          <div
            className="profit-loss-row profit-loss-tappable"
            onClick={handlePLClick}
            onTouchStart={handlePLTouchStart}
            onTouchEnd={handlePLTouchEnd}
          >
            <span
              className={`profit-loss-modern ${(ranking.profitLossPercent || 0) >= 0 ? 'positive' : 'negative'}`}
              style={{ color: (ranking.profitLossPercent || 0) >= 0 ? '#059669' : '#DC2626' }}
            >
              {showDollarPL
                ? formatDollarPL(animatedProfitLossDollar)
                : `${animatedProfitLoss >= 0 ? '+' : ''}${animatedProfitLoss.toFixed(1)}%`
              }
            </span>
            {ranking.profitLossPercent !== null && ranking.profitLossPercent !== undefined && (
              <span className={`trend-arrow ${ranking.profitLossPercent >= 0 ? 'up' : 'down'}`}>
                {ranking.profitLossPercent >= 0 ? '↑' : '↓'}
              </span>
            )}
          </div>
        </div>

        {/* Expand/Actions */}
        <div className="actions-column">
          {canRemoveUser && (
            <button
              onClick={handleRemoveUser}
              disabled={removing}
              className="remove-btn-modern"
              title={`Remove ${userName}`}
              aria-label={`Remove ${userName} from group`}
            >
              {removing ? '...' : '×'}
            </button>
          )}
          <span className={`expand-icon-modern ${expanded ? 'rotated' : ''}`}>
            ▼
          </span>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="ranking-card-details">
          <div className="details-grid">
            <div className="detail-item" data-tooltip="Available cash balance">
              <span className="detail-label">Cash</span>
              <span className="detail-value">{formatCurrency(animatedCash)}</span>
              <div className="detail-percentage" style={{ fontSize: '0.7rem', color: '#6B7280', marginTop: '4px' }}>
                {ranking.netWorth > 0 ? `${((animatedCash / ranking.netWorth) * 100).toFixed(1)}%` : '0%'}
              </div>
            </div>
            <div className="detail-item" data-tooltip="Total portfolio value">
              <span className="detail-label">Investments</span>
              <span className="detail-value">{formatCurrency(animatedPortfolio)}</span>
              <div className="detail-percentage" style={{ fontSize: '0.7rem', color: '#6B7280', marginTop: '4px' }}>
                {ranking.netWorth > 0 ? `${((animatedPortfolio / ranking.netWorth) * 100).toFixed(1)}%` : '0%'}
              </div>
            </div>
            <div className="detail-item" data-tooltip="Total deposits made">
              <span className="detail-label">Deposits</span>
              <span className="detail-value">{formatCurrency(animatedDeposit)}</span>
            </div>
            {ranking.loan > 0 && (
              <div className="detail-item loan" data-tooltip="Outstanding loan balance">
                <span className="detail-label">Loan</span>
                <span className="detail-value">-{formatCurrency(animatedLoan)}</span>
              </div>
            )}
          </div>

          {/* Allocation breakdown if available */}
          {ranking.allocation?.percentages && (
            <div className="allocation-bar">
              <div className="allocation-segment stocks" style={{ width: `${ranking.allocation.percentages.stocks || 0}%` }} title={`Stocks: ${(ranking.allocation.percentages.stocks || 0).toFixed(1)}%`} />
              <div className="allocation-segment etfs" style={{ width: `${ranking.allocation.percentages.etfs || 0}%` }} title={`ETFs: ${(ranking.allocation.percentages.etfs || 0).toFixed(1)}%`} />
              <div className="allocation-segment crypto" style={{ width: `${ranking.allocation.percentages.crypto || 0}%` }} title={`Crypto: ${(ranking.allocation.percentages.crypto || 0).toFixed(1)}%`} />
              <div className="allocation-segment cash" style={{ width: `${ranking.allocation.percentages.cash || 0}%` }} title={`Cash: ${(ranking.allocation.percentages.cash || 0).toFixed(1)}%`} />
            </div>
          )}
        </div>
      )}

      {/* Badges */}
      {isCurrentUser && ranking.rank === 1 && (
        <div style={{ position: 'absolute', top: '12px', right: '16px', zIndex: 5 }}>
          <Badge
            type="leader"
            icon="👑"
            label="Leading!"
            tooltip="You're in first place!"
            pulse={true}
          />
        </div>
      )}
      {ranking.rank <= 3 && !isCurrentUser && (
        <div style={{ position: 'absolute', top: '12px', right: '16px', zIndex: 5 }}>
          <Badge
            type="top-performer"
            icon="⭐"
            label="Top 3"
            tooltip={`Ranked #${ranking.rank} in the group`}
          />
        </div>
      )}

    </div>
  );
};

export default React.memo(GroupRankingCardModern);
